import { CheckCircle2, XCircle, Loader2, Clock, ChevronDown, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface LiveExecutionHeaderProps {
  status: 'idle' | 'running' | 'success' | 'failed' | 'cancelled';
  duration: number;
  completedAt: number | null;
  stats: {
    completedCount: number;
    failedCount: number;
    runningCount: number;
    totalCount: number;
  };
  onToggle: () => void;
}

export function LiveExecutionHeader({
  status,
  duration,
  completedAt,
  stats,
  onToggle,
}: LiveExecutionHeaderProps) {
  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'running':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'success':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Idle';
    }
  };

  return (
    <div className="border-b flex items-center justify-between px-4 py-2 flex-shrink-0 bg-muted/30">
      {/* Left: Status, Duration, and Completed Time */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-sm font-medium">{getStatusLabel()}</span>
        </div>

        <div className="h-4 w-px bg-border" />

        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>{formatDuration(duration)}</span>
        </div>

        {completedAt && status !== 'running' && (
          <>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>{formatDateTime(completedAt)}</span>
            </div>
          </>
        )}
      </div>

      {/* Center: Node Counts */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Completed:</span>
          <span className="font-medium text-green-600">{stats.completedCount}</span>
        </div>
        {stats.failedCount > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Failed:</span>
            <span className="font-medium text-red-600">{stats.failedCount}</span>
          </div>
        )}
        {stats.runningCount > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Running:</span>
            <span className="font-medium text-blue-600">{stats.runningCount}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Total:</span>
          <span className="font-medium">{stats.totalCount}</span>
        </div>
      </div>

      {/* Right: Close Button */}
      <Button variant="ghost" size="sm" onClick={onToggle} className="h-8 w-8 p-0">
        <ChevronDown className="h-4 w-4" />
      </Button>
    </div>
  );
}
